import { Check, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { Alert } from '@/shared/ui/components/basic/Alert';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatCents } from '@/shared/utils/format-cents';
import { totalForSeats } from './plan-flow.items';
import { planIconFor, resolvePlanTier } from '@/shared/ui/components/basic/icons/PlanIcons';
import type { BillingCycle } from '@features/onboarding/hooks/use-plans';
import { CardBrandLogo } from '../SubscriptionPaymentCard';
import { chargesToday, isScheduled, isTrialChange, type PlanFlowKind } from './plan-flow.model';
import { PlanFlowTrialBilling } from './PlanFlowTrialBilling';
import type { PreviewRefusal } from '../../hooks/previewRefusal';

/** One plan as the selector needs it: name, price in the chosen cycle, tier. */
export interface ReviewPlanOption {
  key: string;
  title: string;
  /**
   * Price in cents, fixed at the cycle this option was built for -- not
   * reactive to the cycle toggle. Call {@link priceFor} for the price in
   * whichever cycle is currently selected; using this field directly for
   * that is what made the tile keep showing the yearly price after
   * switching back to monthly.
   */
  priceCents: number;
  /** Per-month equivalent, so a yearly price can be read beside a monthly one. */
  monthlyEquivalentCents: number;
  /** Price in cents for the given cycle, recomputed on every call. */
  priceFor: (cycle: BillingCycle) => number;
  limits?: Record<string, number | null>;
  features?: Record<string, boolean>;
}

interface Props {
  kind: PlanFlowKind;
  options: ReviewPlanOption[];
  currentKey: string;
  /** The cycle the account is actually billed on today, not the one the toggle is pointing at. */
  currentCycle: BillingCycle;
  targetKey: string;
  cycle: BillingCycle;
  yearlyDiscountPercent: number;
  onTargetChange: (key: string) => void;
  onCycleChange: (cycle: BillingCycle) => void;
  /** Pro-rata from the server. Never computed here. */
  proRataCents: number | null;
  daysRemaining: number | null;
  renewalDate: string;
  previewLoading: boolean;
  previewFailed: boolean;
  onRetryPreview: () => void;
  card: { brand: string; lastFour: string } | null;
  cardUpdated: boolean;
  onChangeCard: () => void;
  /** Set after a refused charge, so the buyer is told before trying again. */
  declined: { lastFour: string; amountCents: number } | null;
  /**
   * How many seats the subscription holds.
   *
   * The catalogue prices one seat; a team pays for as many as it has, and
   * every figure on this screen used to print the seat's price as the bill.
   */
  seats?: number;
  /** Set when the change itself was refused, rather than the card. */
  failed?: boolean;
  /** The cycle cannot be changed on this subscription, so the toggle is not offered. */
  cycleLocked?: boolean;
  /** The subscription is in its free period: nothing is charged, the change applies now. */
  trialing?: boolean;
  /** Why the server refused the preview, when it said (no retry offered). */
  previewRefusal?: PreviewRefusal | null;
  /** Free period: the server's figure for the first charge, in cents. */
  nextChargeCents?: number | null;
  /** The server says this change cancels a user reduction scheduled for the next renewal. */
  seatReductionCleared?: boolean;
}

const TIER_ACCENT: Record<string, string> = {
  emerald: 'var(--plan-tier-emerald)',
  ruby: 'var(--plan-tier-ruby)',
  diamond: 'var(--plan-tier-diamond)',
};

/**
 * The review: which plan, on which cycle, what changes, and what it costs.
 *
 * The plan selector lives here rather than in a modal of its own — picking a
 * different target recalculates the whole screen without opening anything.
 * That is what replaces the old compare-then-confirm pair.
 *
 * @param props - Component props.
 * @returns The review body.
 */
export function PlanFlowReview(props: Props) {
  const { t } = useTranslation('features/pricing');
  const {
    kind, options, currentKey, currentCycle, targetKey, cycle, yearlyDiscountPercent,
    onTargetChange, onCycleChange, proRataCents, daysRemaining, renewalDate, seats = 1,
    previewLoading, previewFailed, onRetryPreview, card, cardUpdated,
    onChangeCard, declined, failed = false, trialing, previewRefusal = null, cycleLocked = false,
    nextChargeCents = null, seatReductionCleared = false,
  } = props;

  const current = options.find((o) => o.key === currentKey);
  const target = options.find((o) => o.key === targetKey);
  const immediate = chargesToday(kind);
  const trialChange = isTrialChange(trialing, kind);

  return (
    <>
      {failed && (
        <div
          data-testid="plan-flow-failed"
          className={cn(
            'flex flex-col gap-(--space-1) rounded-(--radius-md)',
            'border border-(--status-error-border) bg-(--status-error-bg)',
            'p-(--space-4)',
          )}
        >
          <Text component="span" className="text-(--status-error-text) [font-weight:var(--font-semibold)]">
            {t('planFlow.failed.title')}
          </Text>
          <Text component="span" className="text-(length:--text-body-sm) text-(--text-secondary)">
            {t('planFlow.failed.body')}
          </Text>
        </div>
      )}

      {declined && (
        <div
          data-testid="plan-flow-declined"
          className={cn(
            'flex flex-col gap-(--space-1) rounded-(--radius-md)',
            'border border-(--status-error-border) bg-(--status-error-bg)',
            'p-(--space-4)',
          )}
        >
          <Text component="span" className="text-(--status-error-text) [font-weight:var(--font-semibold)]">
            {t('planFlow.declined.title')}
          </Text>
          <Text component="span" className="text-(length:--text-body-sm) text-(--text-secondary)">
            {t('planFlow.declined.body', {
              lastFour: declined.lastFour,
              amount: formatCents(declined.amountCents),
            })}
          </Text>
          <UnstyledButton
            onClick={onChangeCard}
            className="self-start text-(length:--text-body-sm) text-(--seed-base) underline"
          >
            {t('planFlow.declined.useAnother')}
          </UnstyledButton>
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-(--space-5)">
        <section className="flex flex-col gap-(--space-4)">
          {current && (
            <Text component="span" className="text-(length:--text-body-sm) text-(--text-secondary)">
              {t('planFlow.review.currentPlanLabel', {
                plan: current.title,
                cycle: t(currentCycle === 'yearly' ? 'glassCards.cycle.yearly' : 'glassCards.cycle.monthly'),
              })}
            </Text>
          )}

          <Text component="span" className="text-(length:--text-small) text-(--text-secondary)">
            {t('planFlow.review.planLabel')}
          </Text>

          {!cycleLocked && (
          <div
            role="group"
            aria-label={t('planFlow.review.cycleLabel')}
            className="flex gap-(--space-1) rounded-(--radius-md) bg-(--surface-tertiary) p-(--space-1)"
          >
            {(['monthly', 'yearly'] as BillingCycle[]).map((option) => (
              <UnstyledButton
                key={option}
                onClick={() => onCycleChange(option)}
                aria-pressed={cycle === option}
                data-testid={`plan-flow-cycle-${option}`}
                data-selected={cycle === option}
                className={cn(
                  'flex flex-1 items-center justify-center gap-(--space-1)',
                  'rounded-(--radius-sm) px-(--space-3) py-(--space-2) text-center',
                  'text-(length:--text-body-sm)',
                  cycle === option
                    ? 'border border-(--seed-base) bg-(--surface-raised) shadow-(--shadow-sm) [font-weight:var(--font-semibold)]'
                    : 'border border-transparent text-(--text-secondary)',
                )}
              >
                {cycle === option && <Check aria-hidden width={14} height={14} className="shrink-0 text-(--seed-base)" />}
                {option === 'monthly'
                  ? t('planFlow.review.cycleMonthly')
                  : t('planFlow.review.cycleYearly', { percent: yearlyDiscountPercent })}
              </UnstyledButton>
            ))}
          </div>
          )}

          <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-(--space-2)">
            {options.map((option) => {
              const selected = option.key === targetKey;
              const Icon = planIconFor(option.key);
              // priceFor(cycle) is the only field here that actually
              // recomputes for the selected cycle -- priceCents is fixed at
              // whichever cycle toFlowOptions built it for (always
              // 'monthly', in practice) and monthlyEquivalentCents is
              // always the annual total divided by 12, regardless of the
              // toggle. Reading either directly is what kept the tile
              // showing the yearly price after switching back to monthly.
              const seatPrice = option.priceFor(cycle === 'yearly' ? 'yearly' : 'monthly');
              const tilePrice = cycle === 'yearly'
                ? t('planFlow.review.perYear', { price: formatCents(totalForSeats(seatPrice, seats) ?? 0) })
                : t('planFlow.review.perMonth', { price: formatCents(totalForSeats(seatPrice, seats) ?? 0) });
              return (
                <UnstyledButton
                  key={option.key}
                  onClick={() => onTargetChange(option.key)}
                  data-testid={`plan-flow-option-${option.key}`}
                  aria-pressed={selected}
                  data-selected={selected}
                  className={cn(
                    'relative flex flex-col items-start gap-(--space-1) rounded-(--radius-md) p-(--space-3) text-left',
                    selected
                      ? 'border-2 border-(--seed-base) bg-[color-mix(in_srgb,var(--seed-base)_8%,var(--surface-raised))] shadow-(--shadow-sm)'
                      : 'border-2 border-transparent bg-(--surface-primary) outline outline-1 -outline-offset-2 outline-(--border-subtle)',
                  )}
                >
                  {selected && (
                    <Text
                      component="span"
                      data-testid={`plan-flow-option-${option.key}-check`}
                      aria-hidden
                      className="absolute end-(--space-2) top-(--space-2) flex h-5 w-5 items-center justify-center rounded-full bg-(--seed-base) text-(--text-inverse)"
                    >
                      <Check width={12} height={12} strokeWidth={3} />
                    </Text>
                  )}
                  <Icon width={14} height={14} color={TIER_ACCENT[resolvePlanTier(option.key)]} />
                  <Text component="span" className="[font-weight:var(--font-bold)]">
                    {option.title}
                  </Text>
                  <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
                    {tilePrice}
                  </Text>
                  {option.key === currentKey && (
                    <Text component="span" className="text-(length:--text-tag) text-(--text-tertiary)">
                      {t('planFlow.review.current')}
                    </Text>
                  )}
                </UnstyledButton>
              );
            })}
          </div>
        </section>

        <section className="flex flex-col gap-(--space-3)">
          <Text component="span" className="text-(length:--text-small) text-(--text-secondary)">
            {t('planFlow.review.billingLabel')}
          </Text>

          {/* 'none' means the tile picked is the plan already on the
              account -- clicking the current plan is indistinguishable
              from having clicked nothing, and the billing rows below only
              restate today's own plan back as if it were a quote. Spelling
              that out here is cheaper than teaching every row below to
              notice it has nothing new to say. */}
          {kind === 'none' && (
            <Text
              component="span"
              data-testid="plan-flow-same-plan-note"
              className="text-(length:--text-body-sm) text-(--text-secondary)"
            >
              {t('planFlow.review.noChange')}
            </Text>
          )}

          {seatReductionCleared && (
            <div data-testid="plan-flow-seat-reduction-cleared">
              <Alert color="blue" icon={<Info aria-hidden width={16} height={16} />}>
                <Text component="span" className="text-(length:--text-body-sm)">
                  {t('planFlow.review.seatReductionCleared')}
                </Text>
              </Alert>
            </div>
          )}

          {trialChange ? (
            <PlanFlowTrialBilling
              refusal={previewRefusal}
              failed={previewFailed}
              loading={!previewFailed && nextChargeCents === null}
              onRetry={onRetryPreview}
              conversionDate={renewalDate}
              planTitle={target?.title ?? ''}
              cycle={cycle}
              nextChargeCents={nextChargeCents}
              seats={seats}
            />
          ) : (
            <div
              data-testid="plan-flow-billing"
              className="flex flex-col divide-y divide-(--border-ultra-subtle) rounded-(--radius-lg) border border-(--border-subtle)"
            >
              <div className="flex flex-col gap-(--space-1) p-(--space-4)">
                <div className="flex items-baseline justify-between gap-(--space-3)">
                  <Text component="span" className="text-(length:--text-body-sm)">
                    {t('planFlow.review.chargedToday')}
                  </Text>
                  {previewFailed ? (
                    <Text component="span" className="text-(length:--text-body-sm) text-(--status-error-text)">
                      {t('planFlow.review.previewFailed')}
                    </Text>
                  ) : (
                    <Text component="span" className="[font-weight:var(--font-bold)]">
                      {previewLoading || (immediate && proRataCents === null)
                        ? t('planFlow.review.calculating')
                        : formatCents(immediate ? proRataCents ?? 0 : 0)}
                    </Text>
                  )}
                </div>
                {/* The pro-rata sentence counts days the preview reports. With
                    no preview there is no count, and printing "0 dias" states a
                    remaining period that is not the one the agency has. */}
                {(!immediate || daysRemaining !== null) && (
                  <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
                    {immediate
                      ? t('planFlow.review.proRataNote', {
                        days: daysRemaining,
                        date: renewalDate,
                      })
                      : t('planFlow.review.keepsUntil', {
                        plan: current?.title ?? '',
                        date: renewalDate,
                      })}
                  </Text>
                )}
                {previewFailed && (
                  <UnstyledButton
                    onClick={onRetryPreview}
                    className="self-start text-(length:--text-body-sm) text-(--seed-base) underline"
                  >
                    {t('planFlow.review.retry')}
                  </UnstyledButton>
                )}
              </div>

              <div className="flex flex-col gap-(--space-1) p-(--space-4)">
                <div className="flex items-baseline justify-between gap-(--space-3)">
                  <Text component="span" className="text-(length:--text-body-sm)">
                    {isScheduled(kind)
                      ? t('planFlow.review.fromRenewal', { date: renewalDate })
                      : t('planFlow.review.nextCharge')}
                  </Text>
                  <Text component="span" className="[font-weight:var(--font-semibold)]">
                    {formatCents(totalForSeats(target?.priceFor(cycle) ?? 0, seats) ?? 0)}
                  </Text>
                </div>
                <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
                  {seats > 1
                    ? t('planFlow.review.seatBreakdown', {
                      count: seats,
                      price: formatCents(target?.priceFor(cycle) ?? 0),
                    })
                    : (isScheduled(kind)
                      ? t('planFlow.review.firstChargeOnNewPlan')
                      : t('planFlow.review.newPlanFee'))}
                </Text>
              </div>
            </div>
          )}

          {(immediate || trialChange) && card && (
            <div
              data-testid="plan-flow-card-line"
              className="flex items-center gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) p-(--space-4)"
            >
              <CardBrandLogo brand={card.brand} />
              <div className="flex min-w-0 flex-col">
                <Text component="span" className="text-(length:--text-body-sm)">
                  {t('planFlow.review.chargeOnCard')}
                </Text>
                <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
                  {`•••• ${card.lastFour}`}
                </Text>
                {cardUpdated && (
                  <Text component="span" className="text-(length:--text-meta) text-(--status-success-text)">
                    {t('planFlow.review.cardUpdated')}
                  </Text>
                )}
              </div>
              <UnstyledButton
                onClick={onChangeCard}
                data-testid="plan-flow-change-card"
                className="ms-auto text-(length:--text-body-sm) text-(--seed-base) underline"
              >
                {t('planFlow.review.changeCard')}
              </UnstyledButton>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default PlanFlowReview;
